import { cn } from '@/lib/utils'

/**
 * Label plus a keycap badge, for a control that owns a shortcut. `surface` picks the keycap's colors:
 * `'tooltip'` (default) for sitting inside a `TooltipContent`, `'content'` for sitting directly on the
 * page, such as a floating hint on a hovered table row, `'sidebar'` for sitting directly on a sidebar
 * nav row — the sidebar has its own dark-first palette, distinct from the page's, so it needs its own
 * tokens rather than `'content'`'s (tuned for a light page surface and washed-out over a dark row).
 * A chord (`G then U`) renders each key in its own cap with "then" as plain connecting text between
 * them, rather than one cap holding the whole string. `emphasizeFirstStep` darkens and bolds the first
 * cap alone — the key already pressed, while a sequence sits armed waiting on the second. `label` is
 * optional: omit it for a bare keycap with nothing to its left, e.g. the single next-key badge a
 * sidebar item broadcasts while a sibling's chord is armed.
 */
export function ShortcutHint({
	label,
	keys,
	surface = 'tooltip',
	emphasizeFirstStep = false,
}: {
	label?: string
	keys: string
	surface?: 'tooltip' | 'content' | 'sidebar'
	emphasizeFirstStep?: boolean
}) {
	const steps = keys.split(' then ')
	const kbdClassBySurface: Record<typeof surface, string> = {
		tooltip: cn(
			'rounded border border-tooltip-foreground/20 bg-tooltip-foreground/15',
			'px-1.5 font-sans text-control',
		),
		sidebar: cn(
			'rounded border border-sidebar-border bg-sidebar-accent',
			'px-1.5 font-sans text-badge text-sidebar-active-text shadow-sm',
		),
		content: 'rounded border bg-muted px-1.5 font-sans text-badge',
	}
	const emphasizedKbdClassBySurface: Record<typeof surface, string> = {
		tooltip: cn(
			'rounded border border-tooltip-foreground/50 bg-tooltip-foreground/35',
			'px-1.5 font-sans text-control font-semibold',
		),
		sidebar: cn(
			'rounded border border-sidebar-ring bg-sidebar-accent-hover',
			'px-1.5 font-sans text-badge text-sidebar-active-text font-semibold shadow-sm',
		),
		content: 'rounded border border-foreground/40 bg-muted px-1.5 font-sans text-badge font-semibold',
	}
	const connectorClassBySurface: Record<typeof surface, string> = {
		tooltip: 'text-tooltip-foreground/70',
		sidebar: 'text-sidebar-inactive-text',
		content: 'text-muted-foreground',
	}
	const wrapperClassBySurface: Record<typeof surface, string> = {
		tooltip: 'flex items-center gap-2',
		sidebar: 'flex items-center gap-1.5 text-badge text-sidebar-inactive-text',
		content: 'flex items-center gap-1.5 text-badge text-muted-foreground',
	}
	const kbdClass = kbdClassBySurface[surface]
	const emphasizedKbdClass = emphasizedKbdClassBySurface[surface]
	const connectorClass = connectorClassBySurface[surface]

	return (
		<span className={wrapperClassBySurface[surface]}>
			{label && <span>{label}</span>}
			<span>
				{steps.map((step, index) => (
					<span key={step}>
						{index > 0 && (
							<>
								{' '}
								<span className={connectorClass}>then</span>{' '}
							</>
						)}
						<kbd className={emphasizeFirstStep && index === 0 ? emphasizedKbdClass : kbdClass}>{step}</kbd>
					</span>
				))}
			</span>
		</span>
	)
}
